import { AlertTriangleIcon, XIcon } from '@langchain/untitled-ui-icons';
import { Button, Modal, ModalDialog } from '@mui/joy';

import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { useCurrentTier } from '@/hooks/useCurrentTier';
import { useWorkspaceId } from '@/hooks/useSwr';
import InfoCircleIcon from '@/icons/InfoCircleIcon.svg?react';
import { TenantUsageLimitsResponse } from '@/types/schema';
import { appOrganizationPath } from '@/utils/constants';

export function TenantUsageLimitsBanner(props: {
  usageLimitsResponse: TenantUsageLimitsResponse;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const { isLegacy, isFree } = useCurrentTier();
  const workspaceId = useWorkspaceId();
  const { description, shouldContactSupport, showEnableBillingLink } =
    getUsageLimitDescription(
      props.usageLimitsResponse,
      () => setIsOpen(false),
      isFree
    );
  const timePeriodStr = getUsageLimitTimePeriodString(
    props.usageLimitsResponse
  );
  if (
    props.usageLimitsResponse.usage_limit_type === 'total_unique_traces' &&
    (isFree || isLegacy)
  ) {
    return <BillingRateLimitBanner />;
  }
  const usagelimitsType =
    props.usageLimitsResponse.usage_limit_type == 'user_defined_monthly_traces'
      ? 'total'
      : 'extended retention';
  return (
    <>
      <div className="flex items-center justify-center gap-2 bg-[#175FAF] px-4 py-2 text-center text-white">
        <InfoCircleIcon className="h-4 w-4" />
        <span>
          You have exceeded your per-{timePeriodStr} {usagelimitsType} trace
          usage limits.{' '}
          <a
            className="cursor-pointer underline"
            onClick={() => setIsOpen(true)}
          >
            Learn more
          </a>
        </span>
      </div>
      <Modal open={isOpen} onClose={() => setIsOpen(false)}>
        <ModalDialog
          variant="outlined"
          role="alertdialog"
          aria-labelledby="alert-dialog-modal-title"
          aria-describedby="alert-dialog-modal-description"
          sx={{ maxWidth: 420 }}
        >
          <div className="flex flex-col items-start gap-3">
            <div className="flex w-full items-center justify-between">
              <div className="inline-flex items-center justify-center rounded-full bg-brand-tertiary p-1">
                <div className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-red-500 to-orange-500 text-white">
                  <AlertTriangleIcon className="size-4" />
                </div>
              </div>

              <button
                type="button"
                className="shrink-0 rounded-md p-0.5 text-tertiary transition-all hover:bg-secondary active:bg-secondary"
                onClick={() => setIsOpen(false)}
                aria-label="Close"
              >
                <XIcon className="size-6" />
              </button>
            </div>
            <div className="flex flex-col gap-1">
              <h2 className="text-xl font-semibold">Usage limits exceeded</h2>
            </div>
          </div>

          <div className="mb-6 mt-3 flex flex-col gap-2 text-tertiary">
            <p>{description}</p>
            <p>
              Check our{' '}
              <Link
                className="cursor-pointer underline"
                to={
                  'https://docs.langchain.com/langsmith/administration-overview#rate-limits'
                }
                target="_blank"
              >
                documentation
              </Link>{' '}
              for more information
            </p>

            {shouldContactSupport && (
              <p>
                If you have more questions, please contact{' '}
                <a
                  href="mailto:support@langchain.dev"
                  className="text-brand-green-400 hover:text-brand-green-400"
                >
                  support@langchain.dev
                </a>{' '}
                to increase your limits.
              </p>
            )}
            {showEnableBillingLink && (
              <p>
                To raise your limits, please add a payment method in{' '}
                <Link
                  className="cursor-pointer underline"
                  to={`/${appOrganizationPath}/${workspaceId}/settings/payments`}
                >
                  settings
                </Link>
                .
              </p>
            )}
          </div>

          <Button
            variant="outlined"
            color="neutral"
            onClick={() => setIsOpen(false)}
          >
            Close
          </Button>
        </ModalDialog>
      </Modal>
    </>
  );
}

function BillingRateLimitBanner() {
  return (
    <div className="flex items-center justify-center gap-2 bg-[#D65622] px-4 py-2 text-center text-sm text-black">
      <div className="flex grow items-center justify-center gap-2">
        <span className="font-bold">Action Needed</span>
        <span className="font-medium">
          Your account is being rate limited. To access full features{' '}
          <Link className="cursor-pointer underline" to={'/settings/payments'}>
            Set up Billing
          </Link>
        </span>
      </div>
    </div>
  );
}

const formatter = new Intl.NumberFormat('en-US', {});

const getUsageLimitTimePeriodString = (
  usageLimitsResponse: TenantUsageLimitsResponse
) => {
  if (
    usageLimitsResponse.usage_limit_type === 'payload_size' ||
    usageLimitsResponse.usage_limit_type === 'events_ingested_per_hour'
  ) {
    return 'hour';
  } else if (
    usageLimitsResponse.usage_limit_type === 'events_ingested_per_minute'
  ) {
    return 'minute';
  } else {
    return 'month';
  }
};

const getUsageLimitDescription = (
  usageLimitsResponse: TenantUsageLimitsResponse,
  closeModal: () => void,
  isFree: boolean
): {
  description: React.ReactNode;
  shouldContactSupport: boolean;
  showEnableBillingLink: boolean;
} => {
  if (
    usageLimitsResponse.tenant_limit == null ||
    !usageLimitsResponse.usage_limit_type
  ) {
    return {
      description: '',
      shouldContactSupport: false,
      showEnableBillingLink: false,
    };
  } else if (
    usageLimitsResponse.usage_limit_type === 'events_ingested_per_hour'
  ) {
    return {
      description: `You have exceeded your rate limit of ${formatter.format(
        usageLimitsResponse.tenant_limit
      )} events ingested per hour. This includes any feedback or runs you have logged to LangSmith.`,
      shouldContactSupport: !isFree,
      showEnableBillingLink: isFree,
    };
  } else if (usageLimitsResponse.usage_limit_type === 'payload_size') {
    return {
      description: `You have exceeded your rate limit of ${formatter.format(
        usageLimitsResponse.tenant_limit / 1000000
      )} MB total payload per hour.`,
      shouldContactSupport: !isFree,
      showEnableBillingLink: isFree,
    };
  } else if (
    usageLimitsResponse.usage_limit_type == 'events_ingested_per_minute'
  ) {
    return {
      description: `You have exceeded your rate limit of ${formatter.format(
        usageLimitsResponse.tenant_limit
      )} events ingested per minute. This includes any feedback or runs you have logged to LangSmith.`,
      shouldContactSupport: false,
      showEnableBillingLink: false,
    };
  } else if (usageLimitsResponse.usage_limit_type == 'total_unique_traces') {
    return {
      description: `You have exceeded your rate limit of ${formatter.format(
        usageLimitsResponse.tenant_limit
      )} total traces this month.`,
      shouldContactSupport: !isFree,
      showEnableBillingLink: isFree,
    };
  } else if (
    usageLimitsResponse.usage_limit_type == 'user_defined_monthly_traces'
  ) {
    return {
      description: (
        <span>
          You have exceeded your configured limit of{' '}
          {formatter.format(usageLimitsResponse.tenant_limit)} traces this
          month. You can modify this limit{' '}
          <Link
            className="cursor-pointer underline"
            to={'/settings/payments'}
            onClick={closeModal}
          >
            on the Billing page.
          </Link>
        </span>
      ),
      shouldContactSupport: false,
      showEnableBillingLink: false,
    };
  } else if (
    usageLimitsResponse.usage_limit_type ==
    'user_defined_monthly_longlived_traces'
  ) {
    return {
      description: (
        <span>
          You have exceeded your configured limit of{' '}
          {formatter.format(usageLimitsResponse.tenant_limit)} extended data
          retention traces this month. You can modify this limit{' '}
          <Link
            className="cursor-pointer underline"
            to={'/settings/payments'}
            onClick={closeModal}
          >
            on the Billing page.
          </Link>
          <div className="mt-4">
            <span className="font-bold">Please Note: </span>
            All features that automatically extend trace data retention will be
            disabled until you increase your limit or reach the end of the
            billing period. This includes feedback, automations, and annotation
            queues.
          </div>
        </span>
      ),
      shouldContactSupport: false,
      showEnableBillingLink: false,
    };
  } else {
    return {
      description: 'You have hit an unknown usage limit.',
      shouldContactSupport: true,
      showEnableBillingLink: false,
    };
  }
};
